import React, { useState, useRef } from 'react';
import { supabase } from '../../supabase/client';
import './ImageUpload.css';

const ImageUpload = ({ currentImage, onImageUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const uploadImage = async (file) => {
    try {
      setUploading(true);
      setError('');

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image must be less than 5MB');
      }

      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `recipe-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

      // Upload to Supabase Storage
      const { data, error: uploadError } = await supabase.storage
        .from('recipe-images')
        .upload(fileName, file);

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('recipe-images')
        .getPublicUrl(fileName);

      onImageUpload(publicUrl);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('[ImageUpload] File input change triggered');
    console.log('[ImageUpload] Files:', e.target.files);
    
    const file = e.target.files?.[0];
    if (file) {
      console.log('[ImageUpload] File selected:', file.name, file.type, file.size);
      
      // Create a copy of the file to prevent it from being garbage collected
      const fileBlob = new Blob([file], { type: file.type });
      const newFile = new File([fileBlob], file.name, { type: file.type });
      
      console.log('[ImageUpload] File copy created, uploading...');
      // Upload immediately to prevent losing the file reference
      await uploadImage(newFile);
    } else {
      console.log('[ImageUpload] No file selected');
    }
    
    // Don't reset input value - this might be causing the issue on Android
    // if (e.target) {
    //   e.target.value = '';
    // }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      uploadImage(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    onImageUpload(null);
    setError('');
  };

  return (
    <div className="image-upload">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        onClick={(e) => {
          // Prevent event bubbling on Android
          e.stopPropagation();
        }}
        onFocus={(e) => {
          // Prevent losing focus on Android
          e.stopPropagation();
        }}
        style={{ display: 'none' }}
        capture={false}
        multiple={false}
      />

      {currentImage ? (
        <div className="image-preview">
          <img src={currentImage} alt="Recipe" className="preview-image" />
          <div className="image-overlay">
            <button
              type="button"
              onClick={handleClick}
              className="change-image-button"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Change Image'}
            </button>
            <button
              type="button"
              onClick={removeImage}
              className="remove-image-button"
              disabled={uploading}
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`upload-area ${dragOver ? 'drag-over' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
        >
          <div className="upload-content">
            {uploading ? (
              <>
                <div className="upload-spinner"></div>
                <p>Uploading image...</p>
              </>
            ) : (
              <>
                <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7,10 12,15 17,10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <p className="upload-text">
                  <span className="upload-primary">Click to upload</span>
                  <span className="upload-secondary">or drag and drop</span>
                </p>
                <p className="upload-hint">PNG, JPG, GIF up to 5MB</p>
              </>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="upload-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;